// Phase SIG-VALIDATE — strict client-side signature rules.
// Mirrors backend services/signature_thresholds.py — tune in both places.

export const MIN_POINTS = 50;
export const MIN_BBOX_WIDTH = 40;
export const MIN_BBOX_HEIGHT = 15;
export const MIN_DURATION_MS = 250;

/**
 * Lightweight predicate used by jest tests + the live pad.
 * @returns {boolean}
 */
export function isValidSignaturePayload({ points = 0, bbox = { w: 0, h: 0 }, durationMs = 0 } = {}) {
    if ((points | 0) < MIN_POINTS) return false;
    if ((bbox.w | 0) < MIN_BBOX_WIDTH) return false;
    if ((bbox.h | 0) < MIN_BBOX_HEIGHT) return false;
    if ((durationMs | 0) < MIN_DURATION_MS) return false;
    return true;
}

/**
 * Validate signature stroke data captured by the pad.
 *
 * @param {Object} input
 * @param {Array<Array<{x:number,y:number,t:number}>>} input.segments
 *   Array of stroke segments. Each segment is an array of points.
 * @param {string=} input.method  "draw" | "type"
 * @param {string=} input.typedText
 * @returns {{ok: boolean, reason: string|null, metrics: object}}
 */
export function validateSignature({ segments = [], method = "draw", typedText = "" } = {}) {
    if (method === "type") {
        const trimmed = (typedText || "").trim();
        return {
            ok: trimmed.length > 0,
            reason: trimmed.length > 0 ? null : "empty",
            metrics: { typedLength: trimmed.length },
        };
    }

    const points = [];
    for (const seg of segments || []) {
        if (!Array.isArray(seg)) continue;
        for (const p of seg) {
            if (!p) continue;
            const x = Number(p.x);
            const y = Number(p.y);
            if (Number.isNaN(x) || Number.isNaN(y)) continue;
            const t = p.t == null ? null : Number(p.t);
            points.push({ x, y, t: Number.isNaN(t) ? null : t });
        }
    }

    if (points.length === 0) {
        return {
            ok: false,
            reason: "empty",
            metrics: { pointCount: 0, bboxWidth: 0, bboxHeight: 0, durationMs: 0 },
        };
    }

    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);
    const bboxWidth = Math.max(...xs) - Math.min(...xs);
    const bboxHeight = Math.max(...ys) - Math.min(...ys);
    const ts = points.map((p) => p.t).filter((t) => t != null);
    const durationMs = ts.length >= 2 ? Math.max(...ts) - Math.min(...ts) : 0;

    const metrics = {
        pointCount: points.length,
        bboxWidth,
        bboxHeight,
        durationMs,
    };

    if (points.length < MIN_POINTS) {
        return { ok: false, reason: "below_point_count", metrics };
    }
    if (bboxWidth < MIN_BBOX_WIDTH || bboxHeight < MIN_BBOX_HEIGHT) {
        return { ok: false, reason: "below_bbox", metrics };
    }
    if (ts.length >= 2 && durationMs < MIN_DURATION_MS) {
        return { ok: false, reason: "below_duration", metrics };
    }
    return { ok: true, reason: null, metrics };
}

export function describeReason(reason) {
    switch (reason) {
        case "empty":
            return "Draw a signature in the box above.";
        case "below_point_count":
            return "Add more detail — keep drawing.";
        case "below_bbox":
            return "Signature is too small — make it wider or taller.";
        case "below_duration":
            return "Slow down — sign at a natural pace.";
        case "no_stroke_data":
            return "Couldn't read your signature — try again or use Clear.";
        case "image_decode_failed":
            return "Signature image was unreadable — try again.";
        default:
            return "Please draw a clearer signature. Try again or use the Clear button.";
    }
}

/**
 * Flatten stroke segments into a transport-ready payload (kept small for
 * backend validation — we keep x, y, t only).
 */
export function serializeStrokes(segments) {
    return (segments || [])
        .filter((s) => Array.isArray(s) && s.length > 0)
        .map((s) => s.map((p) => ({ x: Math.round(p.x), y: Math.round(p.y), t: Math.round(p.t) })));
}
